import type { ReportDimension, ReportListQuery, ReportMetrics } from '@super-mega/server/contracts/analytics'

export const REPORT_LABELS: Record<ReportDimension, string> = {
  organization: '主体',
  customer: '客户',
  salesperson: '业务员',
  site: '地址',
  business: '业务',
  subject: '服务对象'
}
export const REPORT_METRICS = [
  { key: 'orders', title: '新增订单', money: false },
  { key: 'customers', title: '下单客户', money: false },
  { key: 'subjects', title: '下单服务对象', money: false },
  { key: 'billed', title: '新增应收（含调整）', money: true },
  { key: 'received', title: '实际收款', money: true },
  { key: 'refunded', title: '实际退款', money: true },
  { key: 'net', title: '净收款', money: true }
] as const

export type ReportMetricKey = keyof ReportMetrics | 'activity' | 'outstanding' | 'activeRentals'
export const REPORT_METRIC_LABELS: Record<ReportMetricKey, string> = {
  activity: '期间下单数量',
  orders: '新增订单',
  customers: '下单客户',
  subjects: '下单服务对象',
  billed: '新增应收（含调整）',
  received: '实际收款',
  refunded: '实际退款',
  net: '净收款',
  expense: '支出金额',
  paid: '实际付款',
  paymentRefund: '付款退回',
  cashBalance: '现金收支结余',
  ledgerBalance: '收支结余',
  outstanding: '期间账单未收',
  activeRentals: '期间在用租约'
}
type ReportView = {
  description: string
  primary: ReportMetricKey[]
  secondary: ReportMetricKey[]
  rankField: ReportListQuery['sortField']
  rankTitle: string
  trend: ('orders' | 'customers' | 'subjects')[]
  financeTrend: ('billed' | 'received' | 'refunded' | 'net')[]
  detailTitle: string
}
export const REPORT_VIEWS: Record<ReportDimension, ReportView> = {
  organization: {
    description: '对比各主体的收入、支出和收支结余，支出台账按应付日期统计。',
    primary: ['net', 'expense', 'ledgerBalance'],
    secondary: ['orders', 'customers', 'billed', 'outstanding'],
    rankField: 'received',
    rankTitle: '主体收款贡献',
    trend: ['orders', 'customers'],
    financeTrend: ['billed', 'received'],
    detailTitle: '主体经营对比'
  },
  customer: {
    description: '查看客户订单贡献、付款情况与尚未收款的账单，识别重点客户。',
    primary: ['activity', 'received', 'outstanding'],
    secondary: ['orders', 'subjects', 'net'],
    rankField: 'outstanding',
    rankTitle: '客户未收账款',
    trend: ['orders', 'subjects'],
    financeTrend: ['received', 'refunded'],
    detailTitle: '客户贡献与付款'
  },
  salesperson: {
    description: '按订单与账单归属对比业务员的获客、订单和回款表现。',
    primary: ['activity', 'orders', 'net'],
    secondary: ['customers', 'billed', 'refunded'],
    rankField: 'received',
    rankTitle: '业务员回款表现',
    trend: ['orders', 'customers'],
    financeTrend: ['received', 'net'],
    detailTitle: '业务员业绩对比'
  },
  site: {
    description: '对比地址的订单热度与期间租约使用情况，观察客户与服务对象覆盖。',
    primary: ['activity', 'activeRentals', 'orders'],
    secondary: ['customers', 'subjects', 'billed'],
    rankField: 'activeRentals',
    rankTitle: '地址租约使用热度',
    trend: ['orders', 'subjects'],
    financeTrend: ['billed', 'received'],
    detailTitle: '地址使用与订单热度'
  },
  business: {
    description: '比较业务品类的订单需求与应收贡献，了解业务结构和回款趋势。',
    primary: ['activity', 'orders', 'billed'],
    secondary: ['customers', 'received', 'net'],
    rankField: 'billed',
    rankTitle: '业务应收贡献',
    trend: ['orders', 'customers'],
    financeTrend: ['billed', 'net'],
    detailTitle: '业务需求与贡献'
  },
  subject: {
    description: '追踪服务对象的办理订单、应收和付款，查看各对象的业务使用情况。',
    primary: ['activity', 'orders', 'received'],
    secondary: ['billed', 'outstanding', 'refunded'],
    rankField: 'orders',
    rankTitle: '服务对象办理热度',
    trend: ['orders', 'customers'],
    financeTrend: ['billed', 'received'],
    detailTitle: '服务对象办理与付款'
  }
}
